import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalizes quantity input: only digits and one decimal separator,
 * no leading zeros (e.g. "01" -> "1"), allows empty string.
 */
export function normalizeQuantityInput(raw: string): string {
  const s = raw.replace(/[^\d.,]/g, "");
  if (s === "") return "";
  const sepIndex = s.search(/[.,]/);
  const hasSep = sepIndex >= 0;
  const beforeSep = s.slice(0, hasSep ? sepIndex : s.length);
  const afterSep = hasSep ? s.slice(sepIndex + 1).replace(/\D/g, "").slice(0, 3) : "";
  const intNorm = beforeSep.replace(/^0+/, "") || "0";
  return hasSep ? `${intNorm}.${afterSep}` : intNorm;
}

/**
 * Normalizes currency input: only digits and one decimal separator (comma or dot),
 * at most 2 digits after the separator (cents). Allows empty string; no leading zeros.
 */
export function normalizeCurrencyInput(raw: string): string {
  const s = raw.replace(/[^\d.,]/g, "");
  if (s === "") return "";
  const sepIndex = s.search(/[.,]/);
  const hasSep = sepIndex >= 0;
  const beforeSep = s.slice(0, hasSep ? sepIndex : s.length);
  const afterSep = hasSep ? s.slice(sepIndex + 1).replace(/\D/g, "").slice(0, 2) : "";
  const intNorm = beforeSep.replace(/^0+/, "") || "0";
  return hasSep ? `${intNorm}.${afterSep}` : intNorm;
}

/**
 * Parses a currency input string (accepts comma or dot as decimal separator).
 * Returns 0 when invalid or empty.
 */
export function parseCurrencyInput(s: string): number {
  if (s === "") return 0;
  const normalized = s.replace(",", ".");
  const num = parseFloat(normalized);
  return Number.isNaN(num) ? 0 : num;
}

/**
 * Normalizes a decimal input string, allowing only digits plus one decimal separator.
 * Accepts comma or dot, preserves empty input, and limits decimal places.
 */
export function normalizeDecimalInput(raw: string, maxDecimals = 2): string {
  const s = raw.replace(/[^\d.,]/g, "");
  if (s === "") return "";

  const sepIndex = s.search(/[.,]/);
  const hasSep = sepIndex >= 0;
  const beforeSep = s.slice(0, hasSep ? sepIndex : s.length);
  const afterSep = hasSep
    ? s.slice(sepIndex + 1).replace(/\D/g, "").slice(0, maxDecimals)
    : "";
  const intNorm = beforeSep.replace(/^0+(\d)/, "$1");

  return hasSep ? `${intNorm || "0"}.${afterSep}` : intNorm;
}

/**
 * Parses a decimal input string (accepts comma or dot as decimal separator).
 * Returns undefined when the input is empty or invalid.
 */
export function parseOptionalDecimalInput(s: string): number | undefined {
  if (s.trim() === "") return undefined;
  const normalized = s.replace(",", ".");
  const num = parseFloat(normalized);
  return Number.isNaN(num) ? undefined : num;
}
